import { PDFDocument, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import type { ReportWithEvents } from '@/types/report';
import { toVisualOrder } from '@/utils/bidi-text';
import { triggerBlobDownload } from '@/utils/download';
import { toSafeExportFileName } from '@/utils/file-name';
import type { ReportGroupByMode } from '../constants';
import type { ReportExportContext } from '../hooks/useReportExport';
import {
    buildReportExportGroupSummaryParts,
    buildReportExportPagination,
    buildReportExportSummaryGrid,
    measureReportExportDescriptionLineCount,
    formatReportExportEventTimestamp,
    loadReportExportLogoPngForDocx,
    measureReportExportEventDataTextColumnsHeight,
    measureReportExportEventTableHeight,
    measureReportExportSummaryPanelHeight,
    resolveReportExportEventContextColumn,
    type PdfLayoutRow,
    type PromptPdfMeta,
    type PreparedEvent,
    type ReportExportLogoPayload,
} from './reportExportLayout';
import {
    ACCENT,
    CANVAS_HEIGHT,
    CANVAS_WIDTH,
    EVENT_DATA_TABLE_BODY_TOP_GAP,
    EVENT_DATA_TABLE_HEADER_HEIGHT,
    EVENT_DATA_TABLE_SOURCE_TYPE_GAP,
    EVENT_ROW_GAP,
    EVENT_SECONDARY_THUMB_CELL_INSET,
    EVENT_SECONDARY_THUMBS_TOP_GAP,
    layoutReportExportSecondaryThumbnailsRow,
    EVENT_TABLE_GAP,
    fetchImageBytes,
    REPORT_EXPORT_EXTRA_THUMBNAIL_FRAMES,
    LEFT_COL_RATIO,
    MAIN_THUMB_HEIGHT,
    MUTED,
    PAGE_PADDING_X,
    PAGE_PADDING_Y,
    PAGE_RULE,
    PROMPT_BLOCK_TAIL_GAP,
    SUMMARY_COL_GAP,
    SUMMARY_INNER_PAD,
    SUMMARY_ROW_GAP,
    SURFACE,
    TABLE_HEADER_BG,
    TEXT,
} from './reportExportShared';

const PDF_W = 595;
const PDF_H = 842;
const Sx = PDF_W / CANVAS_WIDTH;
const Sy = PDF_H / CANVAS_HEIGHT;

const PDF_EXPORT_FONT_FILES = {
    lgcRegular: 'fonts/pdf_export/NotoSans-Regular.ttf',
    lgcBold: 'fonts/pdf_export/NotoSans-Bold.ttf',
    hebrewRegular: 'fonts/pdf_export/NotoSansHebrew-Regular.ttf',
    hebrewBold: 'fonts/pdf_export/NotoSansHebrew-Bold.ttf',
} as const;

const hexRgb = (hex: string) => {
    const normalized = hex.replace('#', '');
    const r = Number.parseInt(normalized.slice(0, 2), 16) / 255;
    const g = Number.parseInt(normalized.slice(2, 4), 16) / 255;
    const b = Number.parseInt(normalized.slice(4, 6), 16) / 255;
    return rgb(r, g, b);
};

interface ReportPdfFonts {
    regular: PDFFont;
    bold: PDFFont;
    hebrewRegular: PDFFont;
    hebrewBold: PDFFont;
}

const fetchPdfFontBytes = async (relativePath: string): Promise<Uint8Array> => {
    const base = import.meta.env.BASE_URL.endsWith('/')
        ? import.meta.env.BASE_URL
        : `${import.meta.env.BASE_URL}/`;
    const response = await fetch(
        new URL(`${base}${relativePath}`, window.location.href),
        {
            credentials: 'include',
        },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to load PDF font ${relativePath}: ${response.status}`,
        );
    }
    return new Uint8Array(await response.arrayBuffer());
};

const loadReportPdfFonts = async (
    pdfDoc: PDFDocument,
): Promise<ReportPdfFonts> => {
    pdfDoc.registerFontkit(fontkit);
    const [lgcRegular, lgcBold, hebrewRegular, hebrewBold] = await Promise.all([
        fetchPdfFontBytes(PDF_EXPORT_FONT_FILES.lgcRegular).then((bytes) =>
            pdfDoc.embedFont(bytes, { subset: false }),
        ),
        fetchPdfFontBytes(PDF_EXPORT_FONT_FILES.lgcBold).then((bytes) =>
            pdfDoc.embedFont(bytes, { subset: false }),
        ),
        fetchPdfFontBytes(PDF_EXPORT_FONT_FILES.hebrewRegular).then((bytes) =>
            pdfDoc.embedFont(bytes, { subset: false }),
        ),
        fetchPdfFontBytes(PDF_EXPORT_FONT_FILES.hebrewBold).then((bytes) =>
            pdfDoc.embedFont(bytes, { subset: false }),
        ),
    ]);
    return { regular: lgcRegular, bold: lgcBold, hebrewRegular, hebrewBold };
};

const pdfText = (value: string): string =>
    value.normalize('NFC').replace(/\u0000/g, '');

const isHebrewCodePoint = (codePoint: number): boolean =>
    (codePoint >= 0x0590 && codePoint <= 0x05ff) ||
    (codePoint >= 0xfb1d && codePoint <= 0xfb4f);

const pickFontForCodePoint = (
    codePoint: number,
    weight: 'regular' | 'bold',
    fonts: ReportPdfFonts,
): PDFFont => {
    if (isHebrewCodePoint(codePoint)) {
        return weight === 'bold' ? fonts.hebrewBold : fonts.hebrewRegular;
    }
    return weight === 'bold' ? fonts.bold : fonts.regular;
};

const widthOfMixed = (
    text: string,
    sizePdf: number,
    weight: 'regular' | 'bold',
    fonts: ReportPdfFonts,
): number => {
    let total = 0;
    for (const character of text) {
        const font = pickFontForCodePoint(
            character.codePointAt(0)!,
            weight,
            fonts,
        );
        total += font.widthOfTextAtSize(character, sizePdf);
    }
    return total;
};

const wrapLines = (
    text: string,
    fonts: ReportPdfFonts,
    weight: 'regular' | 'bold',
    maxWidthPdf: number,
    sizePdf: number,
): string[] => {
    const result: string[] = [];
    const normalized = text.replace(/\s+/g, ' ').trim();
    if (!normalized) {
        return [''];
    }
    const words = normalized.split(' ');
    let current = '';
    for (const word of words) {
        const candidate = current.length > 0 ? `${current} ${word}` : word;
        if (widthOfMixed(candidate, sizePdf, weight, fonts) <= maxWidthPdf) {
            current = candidate;
            continue;
        }
        if (current.length > 0) {
            result.push(current);
            current = '';
        }
        if (widthOfMixed(word, sizePdf, weight, fonts) <= maxWidthPdf) {
            current = word;
            continue;
        }
        let fragment = '';
        for (const character of word) {
            const next = `${fragment}${character}`;
            if (widthOfMixed(next, sizePdf, weight, fonts) <= maxWidthPdf) {
                fragment = next;
            } else {
                if (fragment.length > 0) {
                    result.push(fragment);
                }
                fragment = character;
            }
        }
        current = fragment;
    }
    if (current.length > 0) {
        result.push(current);
    }
    return result;
};

const baselinePdfY = (
    canvasYFromTop: number,
    canvasFontSize: number,
    ascentRatio: number,
): number => PDF_H - (canvasYFromTop + canvasFontSize * ascentRatio) * Sy;

const drawLineTop = (
    page: PDFPage,
    fonts: ReportPdfFonts,
    weight: 'regular' | 'bold',
    text: string,
    canvasX: number,
    canvasYTop: number,
    canvasFontSize: number,
    ascentRatio: number,
    color: ReturnType<typeof rgb>,
): void => {
    const sizePdf = canvasFontSize * Sy;
    let xPdf = canvasX * Sx;
    const yPdf = baselinePdfY(canvasYTop, canvasFontSize, ascentRatio);
    for (const character of toVisualOrder(text)) {
        const font = pickFontForCodePoint(
            character.codePointAt(0)!,
            weight,
            fonts,
        );
        page.drawText(character, {
            x: xPdf,
            y: yPdf,
            size: sizePdf,
            font,
            color,
        });
        xPdf += font.widthOfTextAtSize(character, sizePdf);
    }
};

const drawLinesTop = (
    page: PDFPage,
    fonts: ReportPdfFonts,
    weight: 'regular' | 'bold',
    lines: string[],
    canvasX: number,
    canvasYTop: number,
    canvasFontSize: number,
    canvasLineHeight: number,
    ascentRatio: number,
    color: ReturnType<typeof rgb>,
): number => {
    lines.forEach((line, index) => {
        if (line.length === 0) {
            return;
        }
        drawLineTop(
            page,
            fonts,
            weight,
            line,
            canvasX,
            canvasYTop + index * canvasLineHeight,
            canvasFontSize,
            ascentRatio,
            color,
        );
    });
    return lines.length * canvasLineHeight;
};

const fillRectCanvas = (
    page: PDFPage,
    x: number,
    y: number,
    width: number,
    height: number,
    fill: ReturnType<typeof rgb>,
): void => {
    page.drawRectangle({
        x: x * Sx,
        y: PDF_H - (y + height) * Sy,
        width: width * Sx,
        height: height * Sy,
        color: fill,
    });
};

const strokeRectCanvas = (
    page: PDFPage,
    x: number,
    y: number,
    width: number,
    height: number,
    stroke: ReturnType<typeof rgb>,
): void => {
    page.drawRectangle({
        x: x * Sx,
        y: PDF_H - (y + height) * Sy,
        width: width * Sx,
        height: height * Sy,
        borderColor: stroke,
        borderWidth: 1,
    });
};

const drawRuleCanvas = (
    page: PDFPage,
    y: number,
    stroke: ReturnType<typeof rgb>,
): void => {
    const yPdf = PDF_H - y * Sy;
    page.drawLine({
        start: { x: PAGE_PADDING_X * Sx, y: yPdf },
        end: { x: (CANVAS_WIDTH - PAGE_PADDING_X) * Sx, y: yPdf },
        thickness: 1,
        color: stroke,
    });
};

const wrapPromptBodyLines = (
    fonts: ReportPdfFonts,
    weight: 'regular' | 'bold',
    bodyText: string,
    firstLineMaxWidthCanvas: number,
    fullWidthCanvas: number,
    canvasFontSize: number,
): string[] => {
    const sizePdf = canvasFontSize * Sy;
    const firstMaxPdf = firstLineMaxWidthCanvas * Sx;
    const fullMaxPdf = fullWidthCanvas * Sx;
    const normalized =
        bodyText.replace(/\s+/g, ' ').trim() || 'Untitled prompt';
    const words = normalized.split(' ');
    const lines: string[] = [];
    let current = '';
    const maxForLine = (lineIndex: number) =>
        lineIndex === 0 ? firstMaxPdf : fullMaxPdf;

    for (const word of words) {
        let remainder = word;
        while (remainder.length > 0) {
            const candidate =
                current.length > 0 ? `${current} ${remainder}` : remainder;
            if (
                widthOfMixed(candidate, sizePdf, weight, fonts) <=
                maxForLine(lines.length)
            ) {
                current = candidate;
                remainder = '';
                continue;
            }
            if (current.length > 0) {
                lines.push(current);
                current = '';
                continue;
            }
            const limit = maxForLine(lines.length);
            let piece = '';
            for (const char of remainder) {
                const next = piece + char;
                if (widthOfMixed(next, sizePdf, weight, fonts) <= limit) {
                    piece = next;
                } else {
                    break;
                }
            }
            if (piece.length === 0) {
                piece = remainder.charAt(0);
            }
            lines.push(piece);
            remainder = remainder.slice(piece.length);
        }
    }
    if (current.length > 0) {
        lines.push(current);
    }
    return lines;
};

const drawHeaderPdf = async (
    pdfDoc: PDFDocument,
    page: PDFPage,
    fonts: ReportPdfFonts,
    pageNumber: number,
    totalPages: number,
    logoPayload: ReportExportLogoPayload | null,
): Promise<void> => {
    const headerY = PAGE_PADDING_Y;
    const logoHeightCanvas = 44;
    if (logoPayload) {
        const png = await pdfDoc.embedPng(logoPayload.data);
        const logoWidthCanvas = (png.width / png.height) * logoHeightCanvas;
        const drawW = logoWidthCanvas * Sx;
        const drawH = logoHeightCanvas * Sy;
        page.drawImage(png, {
            x: PAGE_PADDING_X * Sx,
            y: PDF_H - (headerY + logoHeightCanvas) * Sy,
            width: drawW,
            height: drawH,
        });
    }
    const pageText = `Page ${pageNumber} / ${totalPages}`;
    const sizePdf = 22 * Sy;
    const textWidth = widthOfMixed(pageText, sizePdf, 'regular', fonts);
    drawLineTop(
        page,
        fonts,
        'regular',
        pageText,
        CANVAS_WIDTH - PAGE_PADDING_X - textWidth / Sx,
        headerY + 10,
        22,
        0.78,
        hexRgb(MUTED),
    );
    drawRuleCanvas(page, headerY + 62, hexRgb(PAGE_RULE));
};

const drawSummaryPdf = (
    page: PDFPage,
    fonts: ReportPdfFonts,
    report: ReportWithEvents,
    createdByLabel: string,
): number => {
    let y = PAGE_PADDING_Y + 88;

    const titleText = pdfText(report.name);
    const titleLines = wrapLines(
        titleText,
        fonts,
        'bold',
        (CANVAS_WIDTH - PAGE_PADDING_X * 2) * Sx,
        56 * Sy,
    );
    drawLinesTop(
        page,
        fonts,
        'bold',
        titleLines,
        PAGE_PADDING_X,
        y,
        56,
        64,
        0.72,
        hexRgb(TEXT),
    );
    y += titleLines.length * 64;

    y += 12;
    const subtitle = pdfText(report.description?.trim() || report.name);
    const subtitleLines = subtitle
        .split('\n')
        .flatMap((paragraph) =>
            wrapLines(
                paragraph,
                fonts,
                'regular',
                (CANVAS_WIDTH - PAGE_PADDING_X * 2) * Sx,
                30 * Sy,
            ),
        );
    drawLinesTop(
        page,
        fonts,
        'regular',
        subtitleLines,
        PAGE_PADDING_X,
        y,
        30,
        38,
        0.76,
        hexRgb(MUTED),
    );
    y += subtitleLines.length * 38;

    y += 36;
    const panelX = PAGE_PADDING_X;
    const panelWidth = CANVAS_WIDTH - PAGE_PADDING_X * 2;
    const innerPad = SUMMARY_INNER_PAD;
    const gridInnerWidth = panelWidth - innerPad * 2;
    const colWidthCanvas = (gridInnerWidth - SUMMARY_COL_GAP * 2) / 3;
    const panelHeight = measureReportExportSummaryPanelHeight(
        report,
        gridInnerWidth,
        createdByLabel,
    );

    fillRectCanvas(page, panelX, y, panelWidth, panelHeight, hexRgb(SURFACE));

    const grid = buildReportExportSummaryGrid(report, createdByLabel);
    const baseX = panelX + innerPad;
    let rowY = y + innerPad;

    grid.forEach((row) => {
        let cellX = baseX;
        row.forEach((cell) => {
            drawLineTop(
                page,
                fonts,
                'bold',
                cell.label,
                cellX,
                rowY,
                20,
                0.78,
                hexRgb(ACCENT),
            );
            const valueLines = wrapLines(
                pdfText(cell.value),
                fonts,
                'regular',
                (colWidthCanvas - 4) * Sx,
                24 * Sy,
            );
            drawLinesTop(
                page,
                fonts,
                'regular',
                valueLines,
                cellX,
                rowY + 28,
                24,
                32,
                0.78,
                hexRgb(TEXT),
            );
            cellX += colWidthCanvas + SUMMARY_COL_GAP;
        });
        const rowHeights = row.map((cell) => {
            const lines = wrapLines(
                pdfText(cell.value),
                fonts,
                'regular',
                (colWidthCanvas - 4) * Sx,
                24 * Sy,
            ).length;
            return 28 + Math.max(1, lines) * 32 + SUMMARY_ROW_GAP;
        });
        rowY += Math.max(...rowHeights);
    });

    return y + panelHeight;
};

const embedRaster = async (pdfDoc: PDFDocument, bytes: Uint8Array) => {
    try {
        return await pdfDoc.embedJpg(bytes);
    } catch {
        return pdfDoc.embedPng(bytes);
    }
};

const drawThumbnailPdf = async (
    pdfDoc: PDFDocument,
    page: PDFPage,
    fonts: ReportPdfFonts,
    event: PreparedEvent,
    canvasX: number,
    canvasY: number,
    width: number,
    height: number,
): Promise<void> => {
    fillRectCanvas(page, canvasX, canvasY, width, height, hexRgb(SURFACE));
    const thumbnailBytes = await fetchImageBytes(event.thumbnailUrls[0]);
    let embedded = null;
    if (thumbnailBytes) {
        embedded = await embedRaster(pdfDoc, thumbnailBytes);
    }
    if (embedded) {
        const scale = Math.min(
            (width * Sx) / embedded.width,
            (height * Sy) / embedded.height,
        );
        const drawW = embedded.width * scale;
        const drawH = embedded.height * scale;
        const leftPdf = canvasX * Sx + (width * Sx - drawW) / 2;
        const bottomPdf = PDF_H - (canvasY + height) * Sy;
        const bottomAligned = bottomPdf + (height * Sy - drawH) / 2;
        page.drawImage(embedded, {
            x: leftPdf,
            y: bottomAligned,
            width: drawW,
            height: drawH,
        });
    } else {
        const line = 'No preview image';
        const sizePdf = 22 * Sy;
        const tw = widthOfMixed(line, sizePdf, 'regular', fonts);
        const canvasLeft = canvasX + (width - tw / Sx) / 2;
        drawLineTop(
            page,
            fonts,
            'regular',
            line,
            canvasLeft,
            canvasY + height / 2 - 12,
            22,
            0.78,
            hexRgb(ACCENT),
        );
    }
    strokeRectCanvas(page, canvasX, canvasY, width, height, hexRgb(PAGE_RULE));
};

const drawSecondaryThumbnailsRowPdf = async (
    pdfDoc: PDFDocument,
    page: PDFPage,
    event: PreparedEvent,
    canvasX: number,
    stripTopY: number,
    panelWidth: number,
): Promise<void> => {
    const secondaryUrls = event.thumbnailUrls.slice(
        1,
        1 + REPORT_EXPORT_EXTRA_THUMBNAIL_FRAMES,
    );
    if (secondaryUrls.length === 0) {
        return;
    }
    const count = secondaryUrls.length;
    const { cellWidth, cellHeight, gap, contentWidth } =
        layoutReportExportSecondaryThumbnailsRow(panelWidth, count);
    const rowStartX = canvasX + (panelWidth - contentWidth) / 2;
    const inset = EVENT_SECONDARY_THUMB_CELL_INSET;
    const innerW = Math.max(8, cellWidth - inset * 2);
    const innerH = Math.max(8, cellHeight - inset * 2);

    for (let frameIndex = 0; frameIndex < count; frameIndex++) {
        const boxX = rowStartX + frameIndex * (cellWidth + gap);
        fillRectCanvas(
            page,
            boxX,
            stripTopY,
            cellWidth,
            cellHeight,
            hexRgb(SURFACE),
        );

        const bytes = await fetchImageBytes(secondaryUrls[frameIndex]);
        if (bytes) {
            try {
                const embedded = await embedRaster(pdfDoc, bytes);
                const scale = Math.min(
                    (innerW * Sx) / embedded.width,
                    (innerH * Sy) / embedded.height,
                );
                const drawW = embedded.width * scale;
                const drawH = embedded.height * scale;
                const cellLeftPdf = boxX * Sx;
                const cellBottomPdf = PDF_H - (stripTopY + cellHeight) * Sy;
                const leftPdf = cellLeftPdf + (cellWidth * Sx - drawW) / 2;
                const bottomPdf = cellBottomPdf + (cellHeight * Sy - drawH) / 2;
                page.drawImage(embedded, {
                    x: leftPdf,
                    y: bottomPdf,
                    width: drawW,
                    height: drawH,
                });
            } catch {
                // skip frame if embedding fails
            }
        }

        strokeRectCanvas(
            page,
            boxX,
            stripTopY,
            cellWidth,
            cellHeight,
            hexRgb(PAGE_RULE),
        );
    }
};

const drawEventInfoPanelPdf = async (
    pdfDoc: PDFDocument,
    page: PDFPage,
    fonts: ReportPdfFonts,
    event: PreparedEvent,
    groupBy: ReportGroupByMode,
    canvasX: number,
    canvasY: number,
    width: number,
    height: number,
): Promise<void> => {
    fillRectCanvas(page, canvasX, canvasY, width, height, hexRgb(SURFACE));

    const colEvent = width * 0.55;
    const colSource = width * 0.45;
    const paddingX = 16;
    const contextColumn = resolveReportExportEventContextColumn(event, groupBy);

    fillRectCanvas(
        page,
        canvasX,
        canvasY,
        width,
        EVENT_DATA_TABLE_HEADER_HEIGHT,
        hexRgb(TABLE_HEADER_BG),
    );

    drawLineTop(
        page,
        fonts,
        'bold',
        'Event',
        canvasX + paddingX,
        canvasY + 10,
        22,
        0.78,
        hexRgb(TEXT),
    );
    drawLineTop(
        page,
        fonts,
        'bold',
        contextColumn.label,
        canvasX + colEvent + paddingX,
        canvasY + 10,
        22,
        0.78,
        hexRgb(TEXT),
    );

    const bodyY =
        canvasY +
        EVENT_DATA_TABLE_HEADER_HEIGHT +
        EVENT_DATA_TABLE_BODY_TOP_GAP;
    const timestampLines = wrapLines(
        pdfText(formatReportExportEventTimestamp(event)),
        fonts,
        'regular',
        (colEvent - paddingX * 2) * Sx,
        26 * Sy,
    );
    const timestampBlockHeight = drawLinesTop(
        page,
        fonts,
        'regular',
        timestampLines,
        canvasX + paddingX,
        bodyY,
        26,
        30,
        0.76,
        hexRgb(TEXT),
    );

    const description = event.description?.trim();
    if (description) {
        const descriptionLines = wrapLines(
            pdfText(description),
            fonts,
            'regular',
            (colEvent - paddingX * 2) * Sx,
            22 * Sy,
        );
        const maxLines = measureReportExportDescriptionLineCount(
            description,
            colEvent - paddingX * 2,
        );
        const visibleDescriptionLines = descriptionLines.slice(0, maxLines);
        if (descriptionLines.length > visibleDescriptionLines.length) {
            const last = visibleDescriptionLines.length - 1;
            let line = visibleDescriptionLines[last];
            while (
                line.length > 0 &&
                widthOfMixed(`${line}…`, 22 * Sy, 'regular', fonts) >
                    (colEvent - paddingX * 2) * Sx
            ) {
                line = line.slice(0, -1).trimEnd();
            }
            visibleDescriptionLines[last] = `${line}…`;
        }
        drawLinesTop(
            page,
            fonts,
            'regular',
            visibleDescriptionLines,
            canvasX + paddingX,
            bodyY + timestampBlockHeight + 8,
            22,
            28,
            0.78,
            hexRgb(MUTED),
        );
    }

    const sourceLines = wrapLines(
        pdfText(contextColumn.value),
        fonts,
        'regular',
        (colSource - paddingX * 2) * Sx,
        24 * Sy,
    );
    const sourceBlockHeight = drawLinesTop(
        page,
        fonts,
        'regular',
        sourceLines,
        canvasX + colEvent + paddingX,
        bodyY,
        24,
        28,
        0.78,
        hexRgb(TEXT),
    );

    if (contextColumn.detail) {
        drawLineTop(
            page,
            fonts,
            'regular',
            pdfText(contextColumn.detail),
            canvasX + colEvent + paddingX,
            bodyY + sourceBlockHeight + EVENT_DATA_TABLE_SOURCE_TYPE_GAP,
            18,
            0.78,
            hexRgb(MUTED),
        );
    }

    const textBlockHeight = measureReportExportEventDataTextColumnsHeight(
        event,
        width,
        groupBy,
    );
    const stripTopY = bodyY + textBlockHeight + EVENT_SECONDARY_THUMBS_TOP_GAP;
    await drawSecondaryThumbnailsRowPdf(
        pdfDoc,
        page,
        event,
        canvasX,
        stripTopY,
        width,
    );

    strokeRectCanvas(page, canvasX, canvasY, width, height, hexRgb(PAGE_RULE));
};

const drawPromptHeaderPdf = (
    page: PDFPage,
    fonts: ReportPdfFonts,
    meta: PromptPdfMeta,
    canvasX: number,
    canvasY: number,
    width: number,
): number => {
    if (!meta.showHeader) {
        return canvasY;
    }
    let cursorY = canvasY;
    const prefix = meta.headingLabel;
    const bodyText = pdfText(
        (meta.promptText || 'Untitled prompt').trim() || 'Untitled prompt',
    );

    const prefixSizePdf = 28 * Sy;
    const prefixWidthPdf = fonts.bold.widthOfTextAtSize(prefix, prefixSizePdf);
    const prefixWidthCanvas = prefixWidthPdf / Sx;
    const gap = 16;
    const firstLineWidthCanvas = Math.max(48, width - prefixWidthCanvas - gap);

    drawLineTop(
        page,
        fonts,
        'bold',
        prefix,
        canvasX,
        cursorY + 10,
        28,
        0.72,
        hexRgb(TEXT),
    );

    const bodyLines = wrapPromptBodyLines(
        fonts,
        'regular',
        bodyText,
        firstLineWidthCanvas,
        width,
        40,
    );
    const lineHeight = 46;
    let bodyY = cursorY;
    bodyLines.forEach((line, lineIndex) => {
        const lineX =
            lineIndex === 0 ? canvasX + prefixWidthCanvas + gap : canvasX;
        if (line.length > 0) {
            drawLineTop(
                page,
                fonts,
                'regular',
                line,
                lineX,
                bodyY,
                40,
                0.72,
                hexRgb(MUTED),
            );
        }
        bodyY += lineHeight;
    });

    cursorY = Math.max(bodyY, cursorY + 44);
    cursorY += 14;
    const metaLine = buildReportExportGroupSummaryParts(meta.events).join(
        ' - ',
    );
    drawLineTop(
        page,
        fonts,
        'regular',
        metaLine,
        canvasX,
        cursorY,
        22,
        0.78,
        hexRgb(MUTED),
    );

    return cursorY + 26 + PROMPT_BLOCK_TAIL_GAP;
};

const drawEventRowPdf = async (
    pdfDoc: PDFDocument,
    page: PDFPage,
    fonts: ReportPdfFonts,
    row: PdfLayoutRow,
    canvasX: number,
    canvasY: number,
    width: number,
): Promise<number> => {
    let cursorY = canvasY;
    if (row.showPromptHeader) {
        cursorY = drawPromptHeaderPdf(
            page,
            fonts,
            row.meta,
            canvasX,
            cursorY,
            width,
        );
    }

    const leftWidth = width * LEFT_COL_RATIO - EVENT_TABLE_GAP / 2;
    const rightWidth = width - leftWidth - EVENT_TABLE_GAP;
    const rowTop = cursorY;
    const tableHeight = measureReportExportEventTableHeight(
        row.event,
        leftWidth,
        row.meta.groupBy,
    );

    await drawEventInfoPanelPdf(
        pdfDoc,
        page,
        fonts,
        row.event,
        row.meta.groupBy,
        canvasX,
        rowTop,
        leftWidth,
        tableHeight,
    );

    const thumbX = canvasX + leftWidth + EVENT_TABLE_GAP;
    await drawThumbnailPdf(
        pdfDoc,
        page,
        fonts,
        row.event,
        thumbX,
        rowTop,
        rightWidth,
        MAIN_THUMB_HEIGHT,
    );

    const bottom = Math.max(rowTop + tableHeight, rowTop + MAIN_THUMB_HEIGHT);

    return bottom + EVENT_ROW_GAP;
};

const drawPageContentPdf = async (
    pdfDoc: PDFDocument,
    page: PDFPage,
    fonts: ReportPdfFonts,
    report: ReportWithEvents,
    pageRows: PdfLayoutRow[],
    pageNumber: number,
    totalPages: number,
    createdByLabel: string,
    generatedAt: string,
    logoPayload: ReportExportLogoPayload | null,
): Promise<void> => {
    await drawHeaderPdf(
        pdfDoc,
        page,
        fonts,
        pageNumber,
        totalPages,
        logoPayload,
    );

    let y =
        pageNumber === 1
            ? drawSummaryPdf(page, fonts, report, createdByLabel) + 56
            : PAGE_PADDING_Y + 120;

    if (pageRows.length === 0) {
        const boxW = CANVAS_WIDTH - PAGE_PADDING_X * 2;
        fillRectCanvas(page, PAGE_PADDING_X, y, boxW, 200, hexRgb(SURFACE));
        strokeRectCanvas(page, PAGE_PADDING_X, y, boxW, 200, hexRgb(PAGE_RULE));
        const msg = 'No events match the current report view.';
        const sizePdf = 28 * Sy;
        const tw = widthOfMixed(msg, sizePdf, 'regular', fonts);
        const canvasLeft = PAGE_PADDING_X + (boxW - tw / Sx) / 2;
        drawLineTop(
            page,
            fonts,
            'regular',
            msg,
            canvasLeft,
            y + 84,
            28,
            0.76,
            hexRgb(MUTED),
        );
    } else {
        for (const row of pageRows) {
            y = await drawEventRowPdf(
                pdfDoc,
                page,
                fonts,
                row,
                PAGE_PADDING_X,
                y,
                CANVAS_WIDTH - PAGE_PADDING_X * 2,
            );
        }
    }

    const footer = `Generated ${generatedAt}`;
    const footerSizePdf = 18 * Sy;
    const footerWidth = widthOfMixed(footer, footerSizePdf, 'regular', fonts);
    const footerCanvasX = CANVAS_WIDTH - PAGE_PADDING_X - footerWidth / Sx;
    drawLineTop(
        page,
        fonts,
        'regular',
        footer,
        footerCanvasX,
        CANVAS_HEIGHT - PAGE_PADDING_Y + 8,
        18,
        0.78,
        hexRgb(MUTED),
    );
};

const buildReportVectorPdfBytes = async (
    report: ReportWithEvents,
    viewerUserId: string | undefined,
    viewerDisplayName: string | undefined,
    groupBy: ReportGroupByMode,
    organizationLogoUrl: string | undefined,
): Promise<Uint8Array> => {
    const bundle = await buildReportExportPagination(
        report,
        viewerUserId,
        viewerDisplayName,
        groupBy,
    );
    const pdfDoc = await PDFDocument.create();
    const fonts = await loadReportPdfFonts(pdfDoc);
    const { pageRowSets, createdByLabel, generatedAt } = bundle;
    const totalPages = pageRowSets.length;
    const logoPayload =
        await loadReportExportLogoPngForDocx(organizationLogoUrl);

    for (let index = 0; index < pageRowSets.length; index += 1) {
        const page = pdfDoc.addPage([PDF_W, PDF_H]);
        page.drawRectangle({
            x: 0,
            y: 0,
            width: PDF_W,
            height: PDF_H,
            color: rgb(1, 1, 1),
        });
        await drawPageContentPdf(
            pdfDoc,
            page,
            fonts,
            report,
            pageRowSets[index],
            index + 1,
            totalPages,
            createdByLabel,
            generatedAt,
            logoPayload,
        );
    }

    return pdfDoc.save();
};

export const exportReportToPdf = async ({
    report,
    viewerUserId,
    viewerDisplayName,
    groupBy,
    organizationLogoUrl,
    fileNameSuffix,
}: ReportExportContext): Promise<boolean> => {
    try {
        const pdfBytes = await buildReportVectorPdfBytes(
            report,
            viewerUserId,
            viewerDisplayName,
            groupBy,
            organizationLogoUrl,
        );
        const blob = new Blob([new Uint8Array(pdfBytes)], {
            type: 'application/pdf',
        });
        triggerBlobDownload(
            blob,
            `${toSafeExportFileName(report.name, 'report')}${fileNameSuffix}.pdf`,
        );
        return true;
    } catch {
        return false;
    }
};
